import {
  Supplement,
  Ingredient,
} from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';
import { ProductSection, SectionData } from "@screens/ShopPage/components/FormulaIngredientsSheet";
import { FormulaBenefits } from '@viome/viome-base/src/usecase/Shop/Shop.model';

export interface SectionItems {
  sectionTitle: string;
  productId: number;
  isBigTitle: boolean;
  items: Ingredient[];
}

export const transformSupplementData = (data: Supplement[]) => {
  const _data = data;
  const sections: {title: string; list: SectionItems[]}[] = [];

  _data.forEach((supplement: Supplement) => {
    const sectionItems: SectionItems[] = [];

    // Handle products with ID 4 (Toothpaste) differently
    if (supplement.product_id === 4 && supplement.sub_product) {
      // Add ingredients without blends
      // if (supplement.formula.ingredients.length > 0) {
      sectionItems.push({
        sectionTitle: supplement.sub_product,
        productId: supplement.product_id,
        isBigTitle: true,
        items: supplement.formula.ingredients,
      });
      // }
      // Add blend ingredients
      supplement.formula.blends.forEach((blend) => {
        sectionItems.push({
          sectionTitle: blend.name,
          productId: supplement.product_id,
          isBigTitle: false,
          items: blend.ingredients,
        });
      });

      if (sectionItems.length > 0) {
        sections.push({
          title: supplement.product_name,
          list: sectionItems,
        });
      }
    } else {
      // Handle other products
      // Add ingredients without blends
      // if (supplement.formula.ingredients.length > 0) {
      sectionItems.push({
        sectionTitle: '',
        productId: supplement.product_id,
        isBigTitle: false,
        items: supplement.formula.ingredients,
      });
      // }

      // Add blend ingredients
      supplement.formula.blends.forEach((blend) => {
        sectionItems.push({
          sectionTitle: blend.name,
          productId: supplement.product_id,
          isBigTitle: false,
          items: blend.ingredients,
        });
      });

      if (sectionItems.length > 0) {
        sections.push({
          title: supplement.product_name,
          list: sectionItems,
        });
      }
    }
  });

  const uniqueSections: {title: string; list: SectionItems[]}[] = [];
  sections.forEach((section) => {
    const existingSection = uniqueSections.find(
      (s) => s.title === section.title,
    );
    if (existingSection) {
      existingSection.list = [...existingSection.list, ...section.list];
    } else {
      uniqueSections.push(section);
    }
  });

  return uniqueSections;
};

export const findProductSectionsById = (
  data: SectionData | undefined,
  targetProductId: number,
): ProductSection[] => {
  if (!data) {
    return [];
  }
  const matchingSections: ProductSection[] = [];
  for (const category of data) {
    if (category.list) {
      for (const productSection of category.list) {
        if (productSection.productId === targetProductId) {
          matchingSections.push(productSection);
        }
      }
    }
  }
  return matchingSections;
};

export const constructFormulaBenefitsText = (
  formulaBenefits: FormulaBenefits | undefined | null,
): string => {
  if (!formulaBenefits) {
    return '';
  }

  const {scores, connections, goals} = formulaBenefits;

  const scoreCount = scores?.length || 0;
  const connectionsCount = connections?.length || 0;

  let goalsText = '';
  if (goals && goals.length > 0) {
    if (goals.length === 1) {
      goalsText = goals[0];
    } else if (goals.length === 2) {
      goalsText = `${goals[0]} and ${goals[1]}`;
    } else {
      const lastGoal = goals[goals.length - 1];
      const initialGoals = goals.slice(0, -1).join(', ');
      goalsText = `${initialGoals}, and ${lastGoal}`;
    }
  }

  const parts: string[] = [];
  if (scoreCount > 0) {
    parts.push(`Supports ${scoreCount} score${scoreCount > 1 ? 's' : ''}`);
  }
  if (connectionsCount > 0) {
    parts.push(
      `${connectionsCount} health connection${connectionsCount > 1 ? 's' : ''}`,
    );
  }
  if (goalsText) {
    parts.push(`your self-reported goal for ${goalsText}`);
  }

  if (parts.length === 0) {
    return '';
  }

  if (parts.length === 1) {
    return parts[0] + '.';
  }

  if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]}.`;
  }

  // For 3 or more parts
  const lastPart = parts.pop();
  return `${parts.join(', ')}, and ${lastPart}.`;
};